import type { ActiveFilter } from '@/components/list/list.types'

/**
 * AP-35 Nachtrag 10 — **Filter und Ablage der Arbeitsschritte**, ohne React.
 *
 * Die Filterleiste arbeitet im Browser: Eine Position hat Dutzende Schritte,
 * nicht Tausende, und einen Export der Liste gibt es nicht — die Aufträge
 * druckt jeder Schritt selbst.
 */

/** Freigegeben heißt: Die Ausführung ist freigegeben — auch zugeteilt, in Arbeit und fertig. */
export const SCHRITT_FREIGEGEBEN = ['READY', 'ASSIGNED', 'IN_PROGRESS', 'DONE'] as const

/** Wert der Mehrfachauswahl für „ohne Qualifikation“. */
export const OHNE = '__ohne__'

export interface FilterbarerSchritt {
  phaseId: string
  qualificationId: string | null
  executionStatus: string
  dueAt: string | null
}

const werte = (a: ActiveFilter | undefined) => (a?.value ?? '').split(',').filter(Boolean)

/**
 * Passt der Schritt zu den Filtern der Leiste? Mehrfachauswahl mit „ist“ oder
 * „ist nicht“; „Fertigstellung bis“ lässt Schritte ohne Termin weg — wer bis
 * zu einem Tag fertig sein muss, hat einen Termin.
 */
export function schrittPasst(s: FilterbarerSchritt, filter: Record<string, ActiveFilter>): boolean {
  const mehrfach = (key: string, wert: string) => {
    const w = werte(filter[key])
    if (!w.length) return true
    const drin = w.includes(wert)
    return filter[key]!.operator === 'is_not' ? !drin : drin
  }
  if (!mehrfach('category', s.phaseId)) return false
  if (!mehrfach('status', s.executionStatus)) return false
  if (!mehrfach('qualification', s.qualificationId ?? OHNE)) return false
  const freigabe = filter.released?.value
  if (freigabe) {
    const frei = (SCHRITT_FREIGEGEBEN as readonly string[]).includes(s.executionStatus)
    if ((freigabe === 'yes') !== frei) return false
  }
  const bis = filter.dueUntil?.value
  if (bis && (!s.dueAt || s.dueAt.slice(0, 10) > bis.slice(0, 10))) return false
  return true
}

/** Wie viele Filter gesetzt sind — für den Knopf. */
export function filterAnzahl(filter: Record<string, ActiveFilter>): number {
  return Object.values(filter).filter((a) => a?.value).length
}

// ── Ablage ────────────────────────────────────────────────────────────────

/**
 * Kopierte Schritte liegen in einer **eigenen Ablage** im Browser, nicht in
 * der Zwischenablage für Belegzeilen: Ein Arbeitsschritt ist keine Zeile, die
 * in einem Angebot oder als Position im Projekt etwas zu suchen hätte. Gemerkt
 * werden nur die Ids — eingefügt wird auf dem Server aus dem aktuellen Stand,
 * mit Rechte- und Sichtbarkeitsprüfung. Die Ablage bleibt nach dem Einfügen
 * gefüllt: Dieselben Schritte in mehrere Positionen einzufügen ist der Normalfall.
 */
export interface SchrittAblage {
  userId: string
  stepIds: string[]
  /** „3 Schritte aus 06891-23 / 1“ */
  herkunft: string
}

const ABLAGE = 'symphenia-arbeitsschritte-ablage'
const EREIGNIS = 'symphenia-arbeitsschritte-ablage'

export function leseAblage(userId: string | undefined): SchrittAblage | null {
  try {
    const roh = JSON.parse(localStorage.getItem(ABLAGE) ?? 'null') as SchrittAblage | null
    return roh && roh.userId === userId && Array.isArray(roh.stepIds) && roh.stepIds.length ? roh : null
  } catch {
    return null
  }
}

export function schreibeAblage(ablage: SchrittAblage | null) {
  try {
    if (ablage) localStorage.setItem(ABLAGE, JSON.stringify(ablage))
    else localStorage.removeItem(ABLAGE)
  } catch {
    /* ohne Speicher keine Ablage über das Neuladen hinaus */
  }
  window.dispatchEvent(new Event(EREIGNIS))
}

/** Meldet Änderungen der Ablage — auch aus einem anderen Tab (`storage`). */
export function beobachteAblage(rueckruf: () => void): () => void {
  const aufStorage = (e: StorageEvent) => e.key === ABLAGE && rueckruf()
  window.addEventListener(EREIGNIS, rueckruf)
  window.addEventListener('storage', aufStorage)
  return () => {
    window.removeEventListener(EREIGNIS, rueckruf)
    window.removeEventListener('storage', aufStorage)
  }
}
